/**
 * RADIO.JSX - Composant pour afficher le modèle 3D de la radio
 * 
 * Ce composant charge et affiche le modèle 3D de la radio avec :
 * - Ses matériaux d'origine (matcap, exportés depuis Blender)
 * - Des contrôles Leva pour ajuster position et rotation
 * - Un bouton "Log Values" pour capturer les valeurs actuelles
 * - Un bouton "Reset" pour revenir aux valeurs par défaut
 * 
 * ORIENTATION :
 * - Blender utilise Z-up, Three.js utilise Y-up
 * - La rotation X de Math.PI / 2 (90°) compense cette différence
 * - La rotation Y de -113° oriente la radio vers la caméra
 * 
 * WORKFLOW DE DEBUG :
 * 1. Ajustez position et rotation via les sliders Leva
 * 2. Cliquez sur "Log Values" pour voir les valeurs dans la console
 * 3. Copiez les valeurs affichées dans vos defaults ou config
 */

// ============================================
// IMPORTS
// ============================================

import { useRef } from 'react'
// useRef : Hook pour créer des références mutables (ref vers objets 3D)

import { useFrame } from '@react-three/fiber'
// useFrame : Hook pour accéder à la boucle de rendu (animations)

import { useModel } from '../hooks/useResources'

import { useControls, button } from 'leva'
// useControls : Crée des contrôles GUI dans le panneau Leva
// button : Type de contrôle pour créer des boutons cliquables

import { useSounds } from '../hooks/useSounds'
// useSounds : Hook pour jouer des sons

import { useAudioAnalyser } from '../hooks/useAudioAnalyser'
// useAudioAnalyser : Hook pour analyser l'audio en temps réel

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function Radio({ scale = 1 }) {
  // Props :
  // - scale : Échelle du modèle (défaut: 1)
  
  // ============================================
  // REFS
  // ============================================
  
  // Référence vers le modèle 3D pour l'animation
  const meshRef = useRef()
  
  // Valeur lissée pour animation smooth du shape key "Boom"
  const smoothedBoom = useRef(0)
  
  // ============================================
  // HOOKS AUDIO
  // ============================================
  
  const { toggle, isPlaying } = useSounds()
  // toggle : Fonction pour démarrer/arrêter un son
  // isPlaying : Fonction pour vérifier si un son joue
  
  const { getFrequencyRange } = useAudioAnalyser()
  // getFrequencyRange : Fonction pour extraire une plage de fréquences (0-1)
  
  // ============================================
  // CHARGEMENT DU MODÈLE 3D
  // ============================================
  
  // Charge le modèle GLB avec ses matériaux matcap
  // Le fichier contient la géométrie + les matériaux configurés dans Blender
  const { scene } = useModel('radio')
  // scene : L'objet Three.js Group contenant le modèle complet

  // ============================================
  // CONTRÔLES LEVA - POSITION & ROTATION
  // ============================================
  
  // useControls retourne les valeurs actuelles directement
  // Ces variables se mettent à jour automatiquement quand on change les sliders
  const { posX, posY, posZ, rotationX, rotationY, rotationZ, boom, screenOpacity } = useControls('Radio', {
    
    // Séparateur visuel (non éditable)
    '---1': { value: '--- Position ---', editable: false },
    
    // ==================
    // POSITION (X, Y, Z)
    // ==================
    
    posX: {
      value: -4.5,          // Valeur par défaut : -3 (à gauche du centre)
      min: -10,           // Limite minimale
      max: 10,            // Limite maximale
      step: 0.1,          // Incrément du slider
      label: 'Position X' // Label affiché dans Leva
    },
    posY: {
      value: 0.3,           // Valeur par défaut : 0 (au sol)
      min: -10,
      max: 10,
      step: 0.1,
      label: 'Position Y'
    },
    posZ: {
      value: 0,           // Valeur par défaut : 0 (centre en profondeur)
      min: -10,
      max: 10,
      step: 0.1,
      label: 'Position Z'
    },
    
    // Séparateur visuel
    '---2': { value: '--- Rotation ---', editable: false },
    
    // =====================
    // ROTATION (X, Y, Z)
    // =====================
    // Les rotations sont en RADIANS (Three.js)
    // Pour convertir : degrés × (Math.PI / 180) = radians
    
    rotationX: {
      //value: Math.PI / 2,  // 90° (conversion Z-up Blender → Y-up Three.js)
                           // Cette rotation fait "lever" la radio du plan XZ vers le plan XY
      value: 1.571,  // 90° (conversion Z-up Blender → Y-up Three.js)
      min: -Math.PI,       // -180°
      max: Math.PI,        // +180°
      step: 0.01,          // Incrément fin pour précision
      label: 'Rotation X (rad)'
    },
    rotationY: {
      //value: -113 * Math.PI / 180, // -113° en radians
                                    // Cette rotation oriente la radio vers la caméra
      value: -2.1, 
      min: -Math.PI,       // -180°
      max: Math.PI,        // +180°
      step: 0.01,
      label: 'Rotation Y (rad)'
    },
    rotationZ: {
      value: 0,            // Pas de rotation sur Z (radio "droite")
      min: -Math.PI,
      max: Math.PI,
      step: 0.01,
      label: 'Rotation Z (rad)'
    },
    
    // Séparateur visuel
    '---3': { value: '--- Animation ---', editable: false },
    
    // ===================
    // SHAPE KEY "BOOM"
    // ===================
    // Les shape keys (morph targets) permettent d'animer la géométrie
    // La valeur va de 0 (forme de base) à 1 (forme déformée complètement)
    
    boom: {
      value: 0,            // Valeur par défaut : 0 (pas de déformation)
      min: 0,              // Minimum : forme de base
      max: 1,              // Maximum : déformation complète
      step: 0.01,          // Incrément fin pour une animation fluide
      label: 'Shape Key: Boom'
    },
    
    // Séparateur visuel
    '---4': { value: '--- Matériaux ---', editable: false },
    
    // ===================
    // TRANSPARENCE SCREEN
    // ===================
    // Contrôle l'opacité du mesh "screen" dans le modèle
    // 1 = totalement visible (opaque)
    // 0 = totalement invisible (transparent)
    
    screenOpacity: {
      value: 1,            // Valeur par défaut : 1 (visible à 100%)
      min: 0,              // Minimum : invisible
      max: 1,              // Maximum : opaque
      step: 0.01,          // Incrément fin pour contrôle précis
      label: 'Screen Opacity'
    },
    
    // Séparateur visuel
    '---5': { value: '--- Actions ---', editable: false },
    
    // Bouton "Log Values" : récupère les valeurs depuis le DOM (inputs HTML)
    logValues: button(() => {
      // Récupère les valeurs directement depuis les inputs HTML de Leva
      // Chaque input a un id sous la forme "Radio.nomDeLaVariable"
      const getPosX = parseFloat(document.getElementById('Radio.posX')?.value || posX)
      const getPosY = parseFloat(document.getElementById('Radio.posY')?.value || posY)
      const getPosZ = parseFloat(document.getElementById('Radio.posZ')?.value || posZ)
      const getRotX = parseFloat(document.getElementById('Radio.rotationX')?.value || rotationX)
      const getRotY = parseFloat(document.getElementById('Radio.rotationY')?.value || rotationY)
      const getRotZ = parseFloat(document.getElementById('Radio.rotationZ')?.value || rotationZ)
      
      console.log('📻 Radio - Valeurs actuelles :')
      
      // Affiche la position (X, Y, Z)
      console.log('\n📍 Position :')
      console.log(`  posX: ${getPosX}`)
      console.log(`  posY: ${getPosY}`)
      console.log(`  posZ: ${getPosZ}`)
      
      // Affiche la rotation en radians ET en degrés (plus lisible)
      console.log('\n🔄 Rotation :')
      console.log(`  rotationX: ${getRotX.toFixed(3)} (${(getRotX * 180 / Math.PI).toFixed(1)}°)`)
      console.log(`  rotationY: ${getRotY.toFixed(3)} (${(getRotY * 180 / Math.PI).toFixed(1)}°)`)
      console.log(`  rotationZ: ${getRotZ.toFixed(3)} (${(getRotZ * 180 / Math.PI).toFixed(1)}°)`)
      
      // Affiche le code formaté, prêt à copier-coller
      console.log('\n📋 Code à copier :')
      console.log(`posX: ${getPosX},`)
      console.log(`posY: ${getPosY},`)
      console.log(`posZ: ${getPosZ},`)
      console.log(`rotationX: ${getRotX.toFixed(3)},`)
      console.log(`rotationY: ${getRotY.toFixed(3)},`)
      console.log(`rotationZ: ${getRotZ.toFixed(3)}`)
    }, { label: '📋 Log Values' }),
    
    // Bouton "Reset" : réinitialise toutes les valeurs aux defaults
    reset: button((get, set) => {
      set('Radio', {
        posX: -3,
        posY: 0,
        posZ: 0,
        rotationX: Math.PI / 2,
        rotationY: -113 * Math.PI / 180,
        rotationZ: 0
      })
    }, { label: '🔄 Reset' })
    
  }, { collapsed: true }) // collapsed: true = panneau fermé par défaut

  // ============================================
  // ANIMATION AUDIO-RÉACTIVE (useFrame)
  // ============================================
  
  useFrame(() => {
    // Vérifier que le modèle est chargé
    if (!meshRef.current) return
    
    // ============================================
    // 1. ANALYSE AUDIO (uniquement si un son joue)
    // ============================================
    
    let audioValue = 0
    
    // Vérifier si le son 'mood1' est en train de jouer
    if (isPlaying('mood1')) {
      // Récupérer les basses (0-20% du spectre de fréquences)
      // Les basses sont idéales pour un effet "boom" type haut-parleur
      //(0, 0.3) pour plus de médiums
      const bass = getFrequencyRange(0, 0.2)
      
      // Lissage exponentiel pour éviter les à-coups
      // 0.2 = vitesse de transition (plus haut = plus rapide, plus nerveux)
      //plus rapide (0.3) - plus lent (0.1)
      smoothedBoom.current += (bass - smoothedBoom.current) * 0.2
      
      //plus fort (1.5) - plus faible (0.5)
      //audioValue = smoothedBoom.current
      audioValue = smoothedBoom.current * 1.5 
    } else {
      // Si aucun son ne joue, ramener progressivement la valeur à 0
      smoothedBoom.current += (0 - smoothedBoom.current) * 0.1
      audioValue = smoothedBoom.current
    }
    
    // ============================================
    // 2. CALCUL VALEUR FINALE
    // ============================================
    
    // Option 1 : Audio seul (recommandé pour effet réaliste)
    // Le slider Leva sert uniquement pour les tests manuels
    const finalBoomValue = boom > 0 ? boom : audioValue
    
    // ============================================
    // 2.1 CALCUL OPACITÉ SCREEN (audio-réactive)
    // ============================================
    // L'écran pulse entre invisible (0%) et 20% visible au rythme de l'audio
    // finalBoomValue varie de 0 à 1
    // On veut que la visibilité varie entre 0% et 20%
    // Formule : opacity = finalBoomValue * 0.2
    
    let finalScreenOpacity = 1.0
    
    if (screenOpacity < 1) {
      // Si le slider Leva est modifié, on utilise sa valeur (pour debug)
      finalScreenOpacity = screenOpacity
    } else if (isPlaying('mood1')) {
      // Si la radio joue, l'écran pulse entre invisible et 20% visible
      // finalBoomValue = 0 → opacity = 0.0 (invisible)
      // finalBoomValue = 1 → opacity = 0.2 (20% visible)
      finalScreenOpacity = finalBoomValue * 0.6
    }
    // Sinon (radio éteinte), finalScreenOpacity reste à 1.0 (100% visible)
    
    // ============================================
    // 3. APPLICATION AU SHAPE KEY "BOOM"
    // ============================================
    
    // Parcourir tous les meshes du modèle
    meshRef.current.traverse((child) => {
      if (child.isMesh) {
        // ============================================
        // 3.1 SHAPE KEY "BOOM" (animation audio-réactive)
        // ============================================
        if (child.morphTargetInfluences && child.morphTargetDictionary) {
          // Trouver l'index du shape key "Boom"
          const boomIndex = child.morphTargetDictionary['Boom']
          
          // Si le shape key "Boom" existe dans ce mesh
          if (boomIndex !== undefined) {
            // Appliquer la valeur finale au morph target
            child.morphTargetInfluences[boomIndex] = finalBoomValue
          }
        }
        
        // ============================================
        // 3.2 TRANSPARENCE DU MESH "SCREEN" (audio-réactive)
        // ============================================
        // Applique l'opacité au mesh "screen" calculée depuis l'audio
        if (child.name === 'screen' && child.material) {
          // Activer la transparence sur le matériau (obligatoire pour que opacity fonctionne)
          child.material.transparent = true
          // Appliquer la valeur d'opacité calculée (audio-réactive ou slider Leva)
          child.material.opacity = finalScreenOpacity
          // needsUpdate pour forcer Three.js à mettre à jour le rendu
          child.material.needsUpdate = true
        }
      }
    })
  })

  // ============================================
  // RENDU DU MODÈLE 3D
  // ============================================
  
  // Clone le modèle et configure les ombres
  // Pour qu'un modèle GLTF projette des ombres, il faut l'activer sur tous ses meshes
  const radioClone = scene.clone()
  
  // Parcourt tous les meshes du modèle et active la projection d'ombres
  radioClone.traverse((child) => {
    if (child.isMesh) {
      child.castShadow = true      // Le mesh projette des ombres
      child.receiveShadow = true   // Le mesh reçoit des ombres
      
      // ============================================
      // CLONAGE DU MATÉRIAU POUR LE MESH "SCREEN"
      // ============================================
      // Si c'est le mesh "screen", on clone son matériau pour qu'il soit indépendant
      // Sinon, modifier le matériau affecte tous les meshes qui le partagent
      if (child.name === 'screen' && child.material) {
        child.material = child.material.clone()
      }
      
      // Debug : afficher les noms de tous les meshes (à retirer après vérification)
      // console.log('Mesh trouvé :', child.name)
    }
  })
  
  // ============================================
  // GESTIONNAIRE DE CLIC
  // ============================================
  
  const handleClick = (e) => {
    e.stopPropagation()  // Empêcher la propagation de l'événement
    
    // ============================================
    // CONFIGURATION DU TEMPS DE DÉPART
    // ============================================
    // Tu peux modifier le temps de départ ici (en secondes)
    // - seek: 0 = Démarrer au début
    // - seek: 15 = Démarrer à 15 secondes
    // - seek: 30 = Démarrer à 30 secondes
    const startTime = 35  // ← MODIFIER ICI pour changer le temps de départ
    
    // Toggle le son mood1 (play si arrêté, stop si en cours)
    // Si play : démarre au temps spécifié, puis boucle normalement
    const isNowPlaying = toggle('mood1', { 
      spatialParams: { 
        seek: startTime 
      } 
    })
    
  }
  
  return (
    // primitive : Composant R3F pour rendre un objet Three.js natif
    <primitive 
      ref={meshRef}        // Référence pour accéder au modèle dans useFrame
      object={radioClone}  // Clone du modèle avec ombres configurées
      
      position={[posX, posY, posZ]}  // Position dans l'espace 3D
                                      // [X, Y, Z] où Y est la hauteur
      
      scale={scale}  // Échelle uniforme du modèle
                     // scale={1} = taille d'origine
                     // scale={2} = 2× plus grand
      
      rotation={[rotationX, rotationY, rotationZ]}  // Rotation Euler en radians
                                                     // [X, Y, Z] appliqués dans cet ordre
      
      onClick={handleClick}  // Gestionnaire de clic pour toggle le son mood1
      
      onPointerOver={(e) => {
        e.stopPropagation()
        document.body.style.cursor = 'pointer'  // Changer le curseur au survol
      }}
      
      onPointerOut={(e) => {
        e.stopPropagation()
        document.body.style.cursor = 'default'  // Restaurer le curseur
      }}
    />
  )
}
